<template>
  <div></div>
</template>

<script setup lang="ts">
import { onMounted } from "vue";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useOrganisationsStore } from "@/store/organisations.store";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useCookies } from "@vueuse/integrations/useCookies";
import { BANNER_COOKIE } from "@/constants/banner-cookie";
import { useReports } from "@/modules/PhishTitan/views/Common/Reports/useReports.store";
import { useMicrosoftConnectStore } from "@/store/microsoftConnect.store";
import { useAzureStore } from "@/store/azure.store";
import { useAuth0SessionStore } from "@/store/auth0Session.store";
import { useFlag } from "@/composables/useFlags/useFlag";
import { useAuthorisationStrategy } from "@/authorisation";
import {
  AUTH0_POST_LOGOUT_PARAM,
  buildAuth0LogoutReturnUrl,
} from "@/utils/auth0";
import { getBasePath } from "@/config/runtimeConfig";

const authStore = useAuthenticationStore();
const organisationsStore = useOrganisationsStore();
const licenceStore = useLicenceStore();
const router = useRouter();
const { t } = useI18n();
const { clearCache: clearPowerBICache } = useReports();
const microsoftConnectStore = useMicrosoftConnectStore();
const azureStore = useAzureStore();
const auth0Session = useAuth0SessionStore();
const { isFeatureAvailable: isAuth0LoginEnabled } = useFlag("AUTH0_LOGIN");
const { clearAuthorisationCache } = useAuthorisationStrategy();

const clearAuthCache = () => authStore.clearAuthCache();
const logUserOut = () => authStore.logUserOut();
const clearOrgCache = () => organisationsStore.clearOrgCache();
const clearConnections = () => microsoftConnectStore.clearConnections();
const clearAzureCache = () => azureStore.clearAzureCache();
const clearLicenceCache = () => licenceStore.clearLicenceCache();

const clearLocalState = (): void => {
  const cookies = useCookies();
  cookies.remove(BANNER_COOKIE.READ_ONLY_ROLE_BANNER_CLOSED);
  // The migration banners persist their dismissal at the app base path
  // (COOKIE_SETTINGS), so removal has to target the same path or the cookie
  // survives logout.
  const bannerCookiePath = { path: getBasePath() };
  cookies.remove(
    BANNER_COOKIE.LEGACY_MIGRATION_BANNER_CLOSED,
    bannerCookiePath,
  );
  // SFI banner dismissal is stored per customer (see getSfiMigrationBannerCookieName),
  // so clear every cookie sharing the SFI prefix.
  Object.keys(cookies.getAll())
    .filter((name) =>
      name.startsWith(BANNER_COOKIE.SFI_MIGRATION_BANNER_CLOSED_PREFIX),
    )
    .forEach((name) => cookies.remove(name, bannerCookiePath));
  clearAzureCache();
  clearLicenceCache();
  clearPowerBICache();
  clearAuthCache();
  clearOrgCache();
  clearConnections();
  // The in-memory policy would otherwise outlive `localStorage.clear()` and be
  // inherited by the next sign-in in this tab.
  clearAuthorisationCache();
  localStorage.clear();
};

const resetAllState = async (): Promise<void> => {
  const query = router.currentRoute.value.query;
  const redirect = query.redirect;

  // Auth0's only allow-listed logout URL is this very route, so it is also
  // where Auth0 drops the user once it has cleared its own session. On that
  // second pass everything is already torn down — re-entering the redirect
  // below would bounce app -> Auth0 -> app forever.
  const returningFromAuth0 = Boolean(query[AUTH0_POST_LOGOUT_PARAM]);

  // An Auth0-only session has no JWT for the legacy endpoint to invalidate, so
  // the call would only 401 — and the 401 interceptor's sign-out would race the
  // full-page redirect below. Skip it in that case only; whenever a JWT exists
  // (including alongside an Auth0 session) it still gets invalidated server-side.
  const hasLegacyToken = Boolean(authStore.token?.access_token);
  const skipLegacyLogout =
    !hasLegacyToken && (returningFromAuth0 || auth0Session.authenticated);

  // The flag says the ENVIRONMENT offers Auth0 — not that this user signed in
  // with it. Sending a legacy JWT user to /v2/logout strands them on an Auth0
  // error page: Auth0 knows nothing of their session and 400s the returnTo
  // unless that exact origin+path is in Allowed Logout URLs. Only skip when we
  // positively know the session is legacy-only; an unknown/unhydrated Auth0
  // session still takes the redirect, so a real Auth0 cookie is never left
  // behind (it would silently sign the next visitor back in).
  const isLegacyOnlySession = hasLegacyToken && !auth0Session.authenticated;
  const startAuth0Logout =
    isAuth0LoginEnabled.value && !returningFromAuth0 && !isLegacyOnlySession;

  try {
    if (!skipLegacyLogout) {
      await logUserOut();
    }
  } catch (_err) {
    // ignore logout errors to proceed with clearing state
  } finally {
    clearLocalState();

    if (startAuth0Logout) {
      // Hands off to a full-page redirect (cookies + Auth0 session cleared,
      // then back here with the marker). No router.replace: this document is
      // being replaced, and navigating now would only race the redirect.
      auth0Session.logout(
        buildAuth0LogoutReturnUrl({
          origin: globalThis.location.origin,
          basePath: getBasePath(),
          redirect: typeof redirect === "string" ? redirect : null,
        }),
      );
      return;
    }

    router.replace(
      redirect
        ? { name: "auth-sign-in", query: { redirect } }
        : { name: "auth-sign-in" },
    );
    window.parent.document.title = t("platform");
  }
};

onMounted(() => {
  resetAllState();
});
</script>
